/**
 * Store Pinia des commandes (composition API)
 */
import { defineStore } from "pinia";
import { ref } from "vue";

import { IOrder } from "../models/order";
import { IOrderForm } from "../models/forms/orderForm";
import { IOrderFilter } from "../models/filters/orderFilter";

import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";

import OrderServices from "../services/OrderService";
import DataOperationError from "../models/api/dataOperationError";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useOrderStore = defineStore("orders", () => {
    // State
    const datas = ref<IOrder[]>([]);
    const current = ref<IOrder>({} as IOrder);
    const itemForm = ref<IOrderForm>({} as IOrderForm);
    const isLoading = ref<boolean>(false);

    // Actions

    /**
     * Chargement des données dans le store
     */
    async function load(filter: IOrderFilter) {
        isLoading.value = true;
        const result = await OrderServices.getAll<IOrderFilter, IOrder>(filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.error) {
            datas.value = result.data as IOrder[];
        }

        return result;
    }

    /**
     * Récupère une commande par son identifiant dans le store, sans passer par l'api
     */
    function getOrderById(id: number) {
        current.value = datas.value.find((order) => order.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    /**
     * Récupération de l'objet dans le store
     */
    function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    /**
     * Récupération d'une commande par son ID via l'API
     */
    async function get(id: number) {
        const result = await OrderServices.getById<IOrder>(id);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = result.data as IOrder;

            current.value = result.data as IOrder;
            itemForm.value = toForm(result.data as IOrder);
        } else {
            console.log("Erreur de récupération 'get' => OrderStore" + JSON.stringify(result));
        }

        return { value: result.data, error: result.error };
    }

    /**
     * Enregistrement d'une commande (modification ou création)
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IOrder | IDataOperationResult<IOrder>>;

        if (itemForm.value.id > 0) {
            result = await OrderServices.update(itemForm.value);

            if (!result.error) {
                current.value = result.data as IOrder;
                const index = datas.value.findIndex((a) => a.id == itemForm.value.id);

                if (index >= 0) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERRROR = "Item non trouvé dans le store";
                    return new ServiceOperationResult<IDataOperationResult<IOrder>>(true, { errors: error });
                }
            }
        } else {
            result = await OrderServices.create(itemForm.value);

            if (!result.error) {
                current.value = result.data as IOrder;
                // Ne pas pousser dans datas ici : le handler SignalR MajOrder (Create)
                // s'en charge pour éviter un doublon (SignalR arrive avant la réponse HTTP).
            }
        }

        return result;
    }

    /**
     * Transformation de la commande en formulaire
     */
    function toForm(order: IOrder): IOrderForm {
        const form = {} as IOrderForm;
        Object.assign(form, order);

        form.emittingSociety = order.emittingSociety.id;
        form.quarantineCenter = order.quarantineCenter.id;
        form.schemaCode = order.schemaCode.id;
        form.ctg = order.ctg.id;
        form.destinationCenter = order.destinationCenter?.id ?? 0;
        form.provider = order.provider.id;
        form.status = order.status.id;

        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(): IOrderForm {
        itemForm.value = {
            id: 0,
            emittingSociety: 0,
            quarantineCenter: 0,
            schemaCode: 0,
            ctg: 0,
            destinationCenter: 0,
            provider: 0,
            status: 0,
            deliveryYear: new Date().getFullYear(),
            deliveryWeek: 1,
            orderedQuantity: 0,
            supervisor: "",
            comment: ""
        } as IOrderForm;
        return itemForm.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Actions
        load,
        getOrderById,
        searchStoreById,
        get,
        save,
        toForm,
        newForm,
    };
});

signalRManager.connection.on("MajOrder", (payload: IPayloadStore) => {
    const store = useOrderStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                store.datas.push(payload.data as unknown as IOrder);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                store.datas.splice(idx, 1, payload.data as unknown as IOrder);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
